import styles from './index.module.css'

import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import cn from 'clsx'
import React, { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { LIVE_LIBRARIES } from '@disco/constants/FeatureTypes'
import { clientLibraryUpdate } from '@disco/state-management/actions/client-library-list-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getClientLibraries,
  getClientLibrary,
  getPrimaryClientLibrary,
} from '@disco/state-management/selectors/client-libraries'
import { getUser } from '@disco/state-management/selectors/user'
import captureError from '@disco/utils/captureError'

import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'

import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import { Callout } from 'components/ds'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ChoiceList from 'components/ui/choice-list'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Flex from 'components/ui/flex'
import InlineLink from 'components/ui/inline-link'
import Label from 'components/ui/label'
import PageContainer from 'components/ui/page-container'
import Switch from 'components/ui/switch'
import Text from 'components/ui/text'
import TextField from 'components/ui/text-field'

import { useFormState } from 'hooks/use-form-state'
import { useDispatch, useSelector } from 'hooks/use-redux'

const FORM_FIELDS = [
  'discovery_access_level',
  'is_public',
  'short_description',
  'primary_shares',
]

const LibrariesLibraryAccess = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const libraryId = params.libraryId as unknown as ClientLibraryId
  const dispatch = useDispatch()
  const library: ClientLibrary = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )
  const primaryLibrary: ClientLibrary | null | undefined = useSelector(
    getPrimaryClientLibrary,
  )
  const { clientLibraries } = useSelector(getClientLibraries)
  const user = useSelector(getUser)
  const { client_libraries_allow_multiple_for_discovery } = user.business ?? {}

  const liveLibrariesEnabled: boolean = useFeatureEnabled(LIVE_LIBRARIES)
  const [formState, setFormState, pendingChanges] = useFormState(
    library,
    FORM_FIELDS,
  )
  const [showPrimaryDropdown, setShowPrimaryDropdown] = useState(false)
  const [dropdownValue, setDropdownValue] = useState<number | null>(null)
  const [applyingSettings, setApplyingSettings] = useState(false)
  const isPrimaryLibrary = Boolean(library?.primary)
  const dropdownOptions = useMemo(
    () =>
      clientLibraries
        .filter(l => l.is_live)
        .map(library => {
          return {
            label: library?.name,
            value: library.id,
          }
        }),
    [clientLibraries],
  )

  const handleSubmit = async (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    setApplyingSettings(true)

    try {
      await dispatch(clientLibraryUpdate(libraryId, formState))

      if (dropdownValue && dropdownValue !== primaryLibrary?.id) {
        await dispatch(
          clientLibraryUpdate(
            dropdownValue,
            {
              primary: true,
            },
            {
              disableNotifications: true,
            },
          ),
        )
        setShowPrimaryDropdown(false)
      }
    } catch (error) {
      captureError(error)
    }

    setApplyingSettings(false)
  }

  const libraryNotLive = !library?.is_live || !liveLibrariesEnabled

  return (
    <>
      {library && !library['loading'] && (
        <DocumentHeadTags
          title={`${library['name']} | Access & promotion | DISCO`}
        />
      )}

      <PageContainer.Section
        title={
          <>
            Supervisor Access
            {libraryNotLive &&
              (isPrimaryLibrary ||
                client_libraries_allow_multiple_for_discovery) && (
                <HelpTooltip>
                  To enable this setting, the Catalog needs to be live. Making
                  Catalogs live requires a Discovery Suite subscription.
                </HelpTooltip>
              )}
          </>
        }
      >
        {isPrimaryLibrary || client_libraries_allow_multiple_for_discovery ? (
          <>
            <Row columnsSize={[9]}>
              <div>
                <ChoiceList
                  disabled={libraryNotLive}
                  multiple={false}
                  wrapText={true}
                  title={
                    <>
                      You can opt in your Catalog to be browsable by approved
                      supervisors on DISCO. You can also opt supervisors in to
                      have download access to your Catalog.{' '}
                      <a
                        className="text-link"
                        href="https://support.disco.ac/home/catalogs-guide#enabling-easy-access-to-make-your-catalog-discoverable-by-music-supervisors-on-disco"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Find out more.
                      </a>
                    </>
                  }
                  items={[
                    {
                      label: (
                        <>
                          <b>Easy access</b> – Allow supervisors to discover,
                          browse, & download from my Catalog on their feed & in{' '}
                          <a
                            className="text-link"
                            href="https://support.disco.ac/home/multidisco-search-feature-guide-for-music-supervisors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            MultiDISCO search
                          </a>{' '}
                          (recommended)
                        </>
                      ),
                      value: 'discovery_easy_access',
                    },
                    {
                      label: (
                        <>
                          <b>Limited access</b> – Allow supervisors to discover
                          and browse my Catalog on their feed
                        </>
                      ),
                      value: 'discovery_limited_access',
                    },
                    {
                      label: (
                        <>
                          <b>Private</b> – Keep my Catalog available only to
                          clients I’ve invited or approved
                        </>
                      ),
                      value: 'discovery_private',
                      nodeBottom: (
                        <>
                          {isPrimaryLibrary &&
                            formState['discovery_access_level'] ===
                              'discovery_private' && (
                              <Alert status="warning" icon="warning">
                                By setting a primary Catalog to private, you’ll
                                be limiting the discoverability of this Catalog.{' '}
                                <InlineLink
                                  to="https://support.disco.ac/home/catalogs-guide#primary-catalogs"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Find out more.
                                </InlineLink>
                              </Alert>
                            )}
                        </>
                      ),
                    },
                  ]}
                  selected={formState['discovery_access_level']}
                  onChange={(value: string) =>
                    setFormState('discovery_access_level', value)
                  }
                />
              </div>
            </Row>
            {['discovery_limited_access', 'discovery_easy_access'].includes(
              formState['discovery_access_level'],
            ) && (
              <Row columnsSize={[9]}>
                <div>
                  <TextField
                    disabled={libraryNotLive}
                    label="Describe your Catalog for supervisors"
                    multiline={4}
                    placeholder="Describe your Catalog’s sound, and note anything supervisors might find useful, like clearance and territory information"
                    tooltipId="tooltip_library_settings_describe_supervisors"
                    value={formState['short_description']}
                    onChange={event =>
                      setFormState('short_description', event.target.value)
                    }
                  />
                </div>
              </Row>
            )}
          </>
        ) : (
          <Callout.Root color="blue">
            <Callout.Icon>
              <RxInfoCircled />
            </Callout.Icon>
            <Callout.Text>
              Only your primary catalog can be opted into Supervisor Access.
              Change your primary catalog to enable access.
            </Callout.Text>
          </Callout.Root>
        )}
      </PageContainer.Section>

      <PageContainer.Section
        title="Primary Catalog"
        description={
          <>
            Your primary catalog can be easily linked in other places that you
            share music in DISCO such as in playlist pages, the email creator,
            and inboxes. We recommend that your primary catalog is set to{' '}
            <i>View without login</i> in the settings below.{' '}
            {!isPrimaryLibrary && (
              <a
                className="text-link"
                href="https://support.disco.ac/home/catalogs-guide#enabling-easy-access-to-make-your-catalog-discoverable-by-music-supervisors-on-disco"
                target="_blank"
                rel="noopener noreferrer"
              >
                Find out more.
              </a>
            )}
          </>
        }
      >
        <>
          <Row columnsSize={[9]}>
            <div>
              <div
                className={cn({
                  [styles.disabled]: libraryNotLive,
                })}
              >
                <Text type="paragraph" display="block">
                  {isPrimaryLibrary ? (
                    <>This Catalog is set as your primary Catalog.</>
                  ) : (
                    <>
                      This Catalog is not set as your primary Catalog.{' '}
                      {primaryLibrary && (
                        <>
                          <Link
                            className="text-link"
                            to={`/catalogs/${primaryLibrary.id}`}
                          >
                            {primaryLibrary.name}
                          </Link>{' '}
                          is set as your primary catalog.
                        </>
                      )}
                    </>
                  )}{' '}
                  <Button
                    appearance="inline-link"
                    caption="Change"
                    onClick={() => {
                      setShowPrimaryDropdown(true)
                    }}
                  />
                </Text>

                {showPrimaryDropdown && (
                  <CustomDropdown
                    options={dropdownOptions}
                    value={dropdownValue ?? primaryLibrary?.id ?? null}
                    onChange={option => {
                      setDropdownValue(option?.value)
                      setFormState('primary_library', option?.value)
                    }}
                  />
                )}
              </div>
              {libraryNotLive && (
                <HelpTooltip>Publish your Catalog first</HelpTooltip>
              )}
            </div>
          </Row>
          {isPrimaryLibrary && (
            <Row columnsSize={[9]}>
              <div>
                <Label
                  caption={
                    <>
                      By enabling the setting below, your primary Catalog will
                      be linked on your track and playlist shares.{' '}
                      <a
                        className="text-link"
                        href="https://support.disco.ac/home/catalogs-guide#linking-your-primary-catalog-to-your-business"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Find out more.
                      </a>
                    </>
                  }
                />
                <Flex spacing="small">
                  <Switch
                    value={formState['primary_shares']}
                    onChange={value => {
                      setFormState('primary_shares', value)
                    }}
                  />
                  <Text type="paragraph">
                    Link to my Primary Catalog from playlist and track shares
                  </Text>
                </Flex>
              </div>
            </Row>
          )}
        </>
      </PageContainer.Section>

      <PageContainer.Section title="Access settings">
        <Row columnsSize={[9]}>
          <div>
            <ChoiceList
              disabled={library && library['loading']}
              multiple={false}
              title="View without login allows anyone with the link to browse and stream tracks, but login is required to download or save tracks. Log in required to view means people accessing your Catalog have to log in to browse your Catalog and download files."
              items={[
                {
                  label: 'View without login',
                  value: true,
                },
                {
                  label: 'Log in required to view',
                  value: false,
                },
              ]}
              selected={formState['is_public']}
              onChange={(value: boolean) => setFormState('is_public', value)}
            />
          </div>
        </Row>
      </PageContainer.Section>

      <PageContainer.Footer>
        <ButtonContainer>
          <Button
            appearance="primary"
            caption="Update Catalog settings"
            disabled={!library || library?.loading || !pendingChanges}
            progressIndicator={applyingSettings}
            progressCaption="Loading"
            onClick={handleSubmit}
          />
        </ButtonContainer>
      </PageContainer.Footer>
    </>
  )
}

export default LibrariesLibraryAccess
